import { Outlet } from 'react-router-dom';

import { FeatureFlagGuard } from '@platform/client/feature-flags';
import type { FeatureConfig } from '@platform/client/features/feature-config';

import { FrameworkProvider } from './context/up-api-provider/framework-context';
import { UpApiProvider } from './context/up-api-provider/up-api-provider';
import { craLoader, shouldRevalidateAssessment } from './cra.loader';
import i18n from './locales/i18n';

export default {
  routes: [
    {
      path: 'cyber-resilience-assessment',
      element: (
        <FeatureFlagGuard featureFlag="CYBER_RESILIENCE_ASSESSMENT">
          <UpApiProvider>
            <FrameworkProvider>
              <Outlet />
            </FrameworkProvider>
          </UpApiProvider>
        </FeatureFlagGuard>
      ),
      loader: craLoader,
      shouldRevalidate: shouldRevalidateAssessment,
      children: [
        {
          path: 'dashboard/*',

          children: [
            {
              index: true,
              lazy: () => import('./pages/assessment-home/assessment-home'),
            },
            {
              path: 'no-assessment',
              lazy: () => import('./pages/no-assessment/no-assessment.page'),
            },
            {
              path: 'list',
              lazy: () =>
                import('./pages/assessment-list/assessment-list.page'),
            },
            {
              path: ':assessmentId',
              children: [
                {
                  index: true,
                  lazy: () => import('./pages/overview/overview.page'),
                },
                {
                  path: 'insurability',
                  lazy: () =>
                    import(
                      './pages/insurability-detail/insurability-detail.page'
                    ),
                },
                {
                  path: 'history',
                  lazy: () =>
                    import(
                      './pages/assessment-history/assessment-history.page'
                    ),
                },
                {
                  path: 'quick-start',
                  lazy: () => import('./pages/quick-start/quick-start.page'),
                },
                {
                  path: 'manage-assessment',
                  lazy: () =>
                    import('./pages/manage-assessment/manage-assessment.page'),
                },
                {
                  path: 'products/:productId',
                  lazy: () =>
                    import('./pages/product-detail/product-detail.page'),
                },
                {
                  path: 'groups/:groupId',
                  lazy: () => import('./pages/group-detail/group-detail.page'),
                },
              ],
            },
          ],
        },
        {
          path: 'files',
          children: [
            {
              index: true,
              lazy: () => import('./pages/files-home/files-home.page'),
            },
            {
              path: ':assessmentId',
              children: [
                {
                  index: true,
                  lazy: () =>
                    import('./pages/files-overview/files-overview.page'),
                },
              ],
            },
          ],
        },
        {
          path: 'collaborators',
          children: [
            {
              index: true,
              lazy: () => import('./pages/collaborators/collaborators.page'),
            },
          ],
        },
      ],
    },
  ],

  navigation: ({ featureFlags }) => {
    if (!featureFlags?.CYBER_RESILIENCE_ASSESSMENT) {
      return {};
    }

    return {
      securityJourney: {
        cyberResilienceAssessment: {
          title: i18n.t('cyberResilienceAssessment:title'),
          segment: '/cyber-resilience-assessment/dashboard',
        },
        fileRepository: {
          title: i18n.t('files:fileRepository'),
          segment: '/cyber-resilience-assessment/files',
        },
        collaborators: {
          title: i18n.t('cyberResilienceAssessment:collaborators'),
          segment: '/cyber-resilience-assessment/collaborators',
        },
      },
    };
  },
} satisfies FeatureConfig;
